/* eslint-disable react/static-property-placement */

import React, { ChangeEvent, Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";
import styled from "styled-components";

import { delay } from "@formant/core/common/delay";
import { duration } from "@formant/core/model/duration";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfiguration } from "@formant/core/model/IDeviceConfiguration";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceTeleopHardwareStreamConfiguration } from "@formant/core/model/IDeviceTeleopHardwareStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IFilter } from "@formant/core/model/IFilter";
import { IPartialViewConfiguration } from "@formant/core/model/IPartialViewConfiguration";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { ITeleopViewConfiguration } from "@formant/core/model/ITeleopViewConfiguration";
import { IView } from "@formant/core/model/IView";
import { ModuleMode } from "@formant/core/model/ModuleMode";
import {
    rtcCompressedImageStreamType,
    rtcH264VideoFrameStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { ControlModuleTypes } from "@formant/core/model/SpecialModuleTypes";
import { Uuid } from "@formant/core/model/Uuid";
import { InputElement } from "@formant/ui/component/common/Field";
import { Icon } from "@formant/ui/component/common/Icon";

import { deviceClient } from "../../../clients";
import { ViewContext } from "../../../context/TeleopContext/ViewContext";
import { authStore, observeStore, operateStore } from "../../../stores";
import { ITeleopSession } from "../../../teleop/ITeleopSession";
import { Buttons } from "../../common/Buttons";
import { Heading1 } from "../../common/Heading1";
import { Modal } from "../../common/Modal";
import { Scrollable } from "../../common/Scrollable";
import { SelectField } from "../../common/SelectField";
import { StandardButton } from "../../common/StandardButton";
import { Timer } from "../../common/Timer";
import { IModulesProps, Modules } from "../../Observe/Modules";
import { RecentEvents } from "../../Observe/RecentEvents";
import { Scrubber } from "../../Observe/Timeline/Scrubber";
import { updateConfiguration } from "../../Observe/utils/updateConfiguration";
import { Toolbar } from "../../Operate/Toolbar";
import { getHero } from "../../ViewDevice/ViewDevice.helpers";
import { AudioVideoSetupPopup } from "../AudioVideoSetupPopup";
import { TeleopConnectionStatus } from "../TeleopConnectionStatus";
import { TeleopControls } from "../TeleopControls";
import { TeleopSlider } from "../TeleopModule/TeleopSlider";
import { TeleopView } from "../TeleopView";
import styles from "./index.scss";
import { ITeleopModuleSlotProps, TeleopModuleSlot } from "./TeleopModuleSlot";

export interface ITeleopContextProps {
    id: Uuid;
    device: IDevice;
    view: IView;
    onChangeLayout: (layout: ITeleopLayout) => void;
    onChangeViewId: (id: Uuid) => void;
    onboarding: boolean;
    session: ITeleopSession;
    layout: ITeleopLayout;
    context: IFilter;
}

export interface IConfiguration {
    layout?: ITeleopLayout;
    document?: IDeviceConfigurationDocument;
}

const tutorialLinks = {
    General: "https://docs.formant.io/docs/device-stream-configuration",
};

const isRTCStreamType = ({
    rtcStreamType,
}: Partial<IDeviceTeleopHardwareStreamConfiguration>): boolean =>
    rtcStreamType === rtcH264VideoFrameStreamType ||
    rtcStreamType === rtcCompressedImageStreamType;

const isROSStreamType = ({
    topicType,
}: Partial<IDeviceTeleopRosStreamConfiguration>): boolean =>
    topicType === "formant/H264VideoFrame" ||
    topicType === "sensor_msgs/CompressedImage";

@observer
export class Teleop2Context extends Component<ITeleopContextProps> {
    @observable
    private configured: boolean = false;
    @observable
    private deviceConfiguration!: IDeviceConfiguration;
    @observable
    private helpLink: string = tutorialLinks.General;
    @observable
    private showVideoSetup: boolean = false;
    @observable
    private view: IView = { ...this.props.view };

    scroller = React.createRef<Scrollable>();

    static contextType = ViewContext;
    declare context: React.ContextType<typeof ViewContext>;

    public constructor(props: ITeleopContextProps) {
        super(props);
    }

    public componentDidUpdate() {
        const { session } = this.props;
        const { onboardingShownTerminal } = operateStore;
        if (this.props.onboarding) {
            // check to see if we should show onboarding terminal
            if (
                onboardingShownTerminal === false &&
                session.sessionStatus === "started" &&
                session.ping !== undefined &&
                this.showVideoSetup === false
            ) {
                operateStore.onboardingShownTerminal = true;
                setTimeout(() => {
                    this.showVideoSetup = true;
                }, 2 * duration.second);
            }
        }
    }

    public render() {
        const { showHelp, share } = operateStore;
        const { configured, deviceConfiguration, showVideoSetup } = this;
        const { id, device, session, layout, view } = this.props;

        const {
            sessionStatus,
            connectionStatus,
            ownerUserPeer,
            devicePeer,
            owner,
        } = session;

        // const { deviceDetails, viewId, onChangeViewId } = this.context;

        const document = deviceConfiguration?.document;
        const teleopConfig = document?.teleop;
        const viewConfig = teleopConfig?.views;

        const teleopViewHardwareStreamNames = (
            teleopConfig?.hardwareStreams || []
        )
            .filter(isRTCStreamType)
            .map(_ => _.name);
        const teleopViewCustomStreamNames = (teleopConfig?.customStreams || [])
            .filter(isRTCStreamType)
            .map(_ => _.name);
        const teleopViewRosStreamNames = (teleopConfig?.rosStreams || [])
            .filter(isROSStreamType)
            .map(_ => _.topicName);

        const teleopViewStreamNames = [
            ...teleopViewHardwareStreamNames,
            ...teleopViewCustomStreamNames,
            ...teleopViewRosStreamNames,
        ];

        let viewStreams: ITeleopViewConfiguration[] =
            viewConfig ||
            teleopViewStreamNames.map(_ => ({
                streamName: _,
            })) ||
            [];

        viewStreams = viewStreams.filter(_ => _.streamName !== undefined);

        const TeleopViews = viewStreams.map((_, index) => (
            <TeleopView
                key={index}
                index={index}
                device={device}
                document={deviceConfiguration?.document}
                session={session}
                viewConfig={_}
                className={styles.view}
                onFullscreen={this.onFullscreen}
            />
        ));

        const hero = getHero(layout);

        const HeroModule = (
            <TeleopView
                key={0}
                index={0}
                device={device}
                document={deviceConfiguration?.document}
                session={session}
                viewConfig={{ streamName: hero || viewStreams[0]?.streamName }}
                className={styles.view}
                onFullscreen={this.onFullscreen}
            />
        );

        const isConnected = connectionStatus === "connected";
        const shouldShowHelpDialog =
            isConnected &&
            teleopConfig &&
            (TeleopViews.length === 0 || showHelp);

        const moduleSlotProps: ITeleopModuleSlotProps = {
            visible: isConnected,
            className: styles["view-slot"],
        };

        const customNumericStreams =
            teleopConfig?.customStreams?.filter(
                _ => _.rtcStreamType === "numeric" && _.mode === "command",
            ) || [];

        const rosNumericStreams =
            teleopConfig?.rosStreams?.filter(
                _ => _.topicType === "std_msgs/Float64" && _.mode === "command",
            ) || [];

        const sliders = [...customNumericStreams, ...rosNumericStreams]
            .filter(
                ({ numericControlVisualization }) =>
                    numericControlVisualization === "slider" ||
                    numericControlVisualization === undefined,
            )
            .map((_, index) => (
                <TeleopSlider key={index} stream={_} session={session} />
            ));

        const dials = [...customNumericStreams, ...rosNumericStreams]
            .filter(
                ({ numericControlVisualization }) =>
                    numericControlVisualization === "dial",
            )
            .map((_, index) => (
                <TeleopSlider key={index} stream={_} session={session} />
            ));

        const moduleSlots = (
            <>
                <TeleopModuleSlot {...moduleSlotProps}>
                    {TeleopViews[1]}
                </TeleopModuleSlot>
                <TeleopModuleSlot {...moduleSlotProps}>
                    {TeleopViews[2]}
                </TeleopModuleSlot>
            </>
        );

        const sidebarPages = [];
        if (
            !authStore.featureEnabled("teleop2.0") &&
            (TeleopViews[1] || TeleopViews[2])
        ) {
            sidebarPages.push(moduleSlots);
        }
        if (sliders.length || dials.length) {
            sidebarPages.push(
                <div className={styles.numerics}>
                    {sliders.length > 0 && (
                        <div className={styles.sliders}>{sliders}</div>
                    )}
                    {dials.length > 0 && (
                        <div className={styles.dials}>{dials}</div>
                    )}
                </div>,
            );
        }

        const configuring =
            observeStore.isCustomizing ||
            observeStore.configuringFlyout !== undefined;

        const extra: Partial<IModulesProps> = authStore.hasRequiredAccess(
            "teleop",
            "administrator",
        )
            ? {
                  onChangeLayout: configuring
                      ? this.props.onChangeLayout
                      : undefined,
                  onRemoveModule: configuring
                      ? this.context.onRemoveModule
                      : undefined,
                  editingLayout: configuring,
              }
            : {
                  onChangeLayout: (_: ITeleopLayout) => {},
                  editingLayout: false,
              };

        const ScrollableModules = (
            <Scrollable ref={this.scroller} className={styles["edit-layout"]}>
                <Modules
                    layout={layout}
                    context={this.props.context}
                    configuration={view.configuration}
                    hideDevice={false}
                    device={device}
                    onConfigureModule={this.onConfigureModule}
                    onChangeConfiguration={this.onChangeConfiguration}
                    moduleMode={ModuleMode.Teleop}
                    {...extra}
                />
            </Scrollable>
        );

        const configModal = (
            <Modal className={styles["accept-terms"]}>
                <div className={styles.centered}>
                    <Icon name="video" size="48" />
                    <Heading1>Sorry, camera not detected</Heading1>
                    <p>To setup a device manually choose a device type</p>
                </div>
                <p>
                    <SelectField
                        label="Device type"
                        small={true}
                        value="General"
                        onChange={this.onChangeHelpDevice}
                    >
                        {Object.entries(tutorialLinks).map(([key, value]) => (
                            <option key={key} value={value}>
                                {key}
                            </option>
                        ))}
                    </SelectField>
                </p>
                <Buttons className={styles.buttons}>
                    <StandardButton primary={true} onClicked={this.onGotoHelp}>
                        Setup Device
                    </StandardButton>
                </Buttons>
            </Modal>
        );

        const filter = { deviceIds: [id] };
        const showConfigModal = !showVideoSetup && shouldShowHelpDialog;

        const controlTypes: string[] = [...ControlModuleTypes];
        const controlModules = Object.keys(view.layout).filter(t =>
            controlTypes.includes(t),
        );
        return (
            <div>
                <AudioVideoSetupPopup
                    id={id}
                    visible={showVideoSetup}
                    onClose={this.onCloseOnboardingVideoSetup}
                />
                <div className={styles["teleop-context"]}>
                    <div className={styles["teleop-scrubber"]}>
                        <Scrubber
                            context={this.props.context}
                            filter={filter}
                            onSelectEvent={() => {}}
                            seekOption={observeStore.seekOption}
                        />
                    </div>
                    {teleopConfig &&
                        controlModules.length === 0 &&
                        !observeStore.isCustomizing && (
                            <div className={styles.controls}>
                                <TeleopControls
                                    sticky={true}
                                    session={session}
                                    configuration={teleopConfig}
                                />
                            </div>
                        )}
                    {!showConfigModal && (
                        <div className={classnames("content", styles.overlay)}>
                            <div className={styles.status}>
                                <TeleopConnectionStatus
                                    sessionStatus={sessionStatus}
                                    connectionStatus={connectionStatus}
                                    ownerUserId={ownerUserPeer?.userId}
                                    devicePeer={devicePeer}
                                    owner={owner}
                                    deviceName={device.name}
                                    onJoin={this.confirmSession}
                                />
                            </div>
                            <ToolBarWrapper>
                                <Toolbar
                                    configuration={document?.teleop}
                                    device={device}
                                />
                            </ToolBarWrapper>
                            {ScrollableModules}
                        </div>
                    )}
                    {!configured && (
                        <Timer
                            interval={2 * duration.second}
                            onTick={this.getDeviceConfigurationStreams}
                        />
                    )}
                    <div className={styles["view-container"]}>
                        {showConfigModal && configModal}
                        {!showConfigModal && (
                            <>
                                <TeleopModuleSlot
                                    {...moduleSlotProps}
                                    className={styles["primary-view"]}
                                    fullscreen={true}
                                >
                                    {HeroModule}
                                </TeleopModuleSlot>
                                {/* <Fader
                                    visible={isConnected}
                                    className={styles.sidebar}
                                >
                                    <div
                                        className={styles.views}
                                        style={{
                                            marginBottom:
                                                operateStore.controlsDimensions
                                                    ?.y || undefined
                                        }}
                                    >
                                        <TabPagination
                                            pages={sidebarPages}
                                            className={
                                                styles["teleop-view-preview"]
                                            }
                                        />
                                    </div>
                                    <FlexSpacer />
                                </Fader> */}
                            </>
                        )}
                    </div>

                    {!share && (
                        <RecentEvents
                            className={styles.events}
                            filter={{ deviceIds: [id] }}
                            onSelect={this.onSelectEvent}
                        />
                    )}
                </div>
            </div>
        );
    }

    private onCloseOnboardingVideoSetup = () => {
        this.showVideoSetup = false;
    };

    private onFullscreen = (index: number) => {
        const { deviceConfiguration } = this;
        const { teleop } = deviceConfiguration?.document || {};
        if (!teleop?.views) {
            return;
        }
        const { views } = teleop;

        const previouslyFullscreened = views[0];
        const toBeFullscreened = views[index];

        views[0] = toBeFullscreened;
        views[index] = previouslyFullscreened;

        teleop.views = views;
    };

    private onChangeHelpDevice = (event: ChangeEvent<InputElement>) => {
        this.helpLink = event.target.value;
    };

    private onGotoHelp = () => {
        window.open(this.helpLink);
    };

    private onSelectEvent = (eventOrAggregate: IEventOrAggregate) => {
        const { event, aggregate } = eventOrAggregate;
        if (event) {
            observeStore.viewEvent(event);
        } else if (aggregate) {
            observeStore.showEventList(aggregate);
        }
    };

    private confirmSession = async () => {
        const { session } = this.props;
        let confirmTries = 0;
        while (confirmTries < 3) {
            confirmTries++;
            if (session) {
                break;
            } else {
                await delay(0.5);
            }
        }

        await session.join();
    };

    private getDeviceConfigurationStreams = async () => {
        const { id, session, device } = this.props;

        const version = device.desiredConfigurationVersion;
        if (!version) {
            return;
        }
        this.deviceConfiguration = await deviceClient.getConfiguration(
            await authStore.token(),
            id,
            version,
        );

        session.deviceConfiguration = this.deviceConfiguration;

        this.configured = true;
    };

    private onConfigureModule = (name: string) => {
        const { layout, onChangeLayout } = this.props;
        observeStore.configuringModule = [name, layout, onChangeLayout];
    };

    private onChangeConfiguration: (
        configuration: IPartialViewConfiguration,
    ) => void = ({ streamName, type, updates }) => {
        const { configuration } = this.view;
        this.view.configuration = updateConfiguration(
            configuration,
            streamName,
            type,
            updates,
        );
    };
}

const ToolBarWrapper = styled.div.withConfig({
    displayName: "ToolBarWrapper",
})`
    zindex: 20;
`;
